import { QueryHookOptions, QueryResult } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useHistory } from 'react-router-dom';

import * as graphql from '@treams/treams-network';
import { Callout, Modal, Pagination, Table, Tabs } from '@treams/treams-ui';

function ConnectorsDryRunTable<
	Q extends graphql.ConnectorDryRunGroupsQuery | graphql.ConnectorDryRunUsersQuery,
	P extends Exclude<
		keyof graphql.ConnectorDryRun &
			keyof NonNullable<Q['viewer']['workspace']['connector']>['dryRun'],
		'__typename'
	>,
>(props: {
	query: (
		options: QueryHookOptions<Q, graphql.ConnectorDryRunUsersQueryVariables>,
	) => QueryResult<Q, graphql.ConnectorDryRunUsersQueryVariables>;
	path: P;
	columns: (keyof graphql.ConnectorDryRun[P]['edges'][0]['node'] & string)[];
}) {
	const { t } = useTranslation();

	const [pagination, setPagination] = useState<Pagination>({ first: 10 });
	const query = props.query({ variables: { ...pagination } });

	const dryRun = query.data?.viewer.workspace.connector?.dryRun as
		| graphql.ConnectorDryRun
		| undefined;

	const noResults = !dryRun || !dryRun[props.path].edges.length;

	if (noResults) {
		return (
			<Callout
				color="blue"
				title={t('connectors.settings.configuration.dryRun.table.no-results.title')}
				description={t('connectors.settings.configuration.dryRun.table.no-results.content')}
			/>
		);
	}

	return (
		<>
			<Table>
				<Table.Header>
					{props.columns.map((column, i) => (
						<Table.Cell key={i} basis={1 / props.columns.length}>
							{t(
								`connectors.settings.configuration.dryRun.table.${props.path}.columns.${column}.title`,
							)}
						</Table.Cell>
					))}
				</Table.Header>
				{dryRun?.[props.path].edges.map(({ node, cursor }) => (
					<Table.Row key={cursor}>
						{props.columns.map((column, i) => (
							<Table.Cell key={i} basis={1 / props.columns.length}>
								{node[column]}
							</Table.Cell>
						))}
					</Table.Row>
				))}
			</Table>
			<Table.Paging pageInfo={dryRun?.[props.path].pageInfo} onPaginate={setPagination} />
		</>
	);
}

export default function ConnectorsDryRun(props: { type: graphql.ConnectorType }) {
	const { t } = useTranslation();
	const history = useHistory();

	const url = `/settings/integrations/${props.type.toLowerCase()}`;

	return (
		<Modal>
			<Modal.Header
				title={t('connectors.settings.configuration.dryRun.title')}
				onClose={() => {
					history.push(url);
				}}
				tabs
			/>
			<Tabs>
				<Tabs.Item
					title={t('connectors.settings.configuration.dryRun.table.groups.title')}
					path={`${url}/results/groups`}
				/>
				<Tabs.Item
					title={t('connectors.settings.configuration.dryRun.table.users.title')}
					path={`${url}/results/users`}
				/>
			</Tabs>
			<Modal.Contents height={500}>
				<Switch>
					<Route path="/settings/integrations/:id/results/groups">
						<ConnectorsDryRunTable
							query={graphql.useConnectorDryRunGroupsQuery}
							path="groups"
							columns={['name', 'externalReferenceID', 'parentExternalReferenceID']}
						/>
					</Route>
					<Route path="/settings/integrations/:id/results/users">
						<ConnectorsDryRunTable
							query={graphql.useConnectorDryRunUsersQuery}
							path="users"
							columns={['firstName', 'lastName']}
						/>
					</Route>
				</Switch>
			</Modal.Contents>
		</Modal>
	);
}
